import * as THREE from "three";
import { CITIES } from "../data/corridors";
import { latLngToPlane } from "./projection";
import { colorNum } from "../theme/tokens";
import nodeVert from "./shaders/node.vert.glsl?raw";
import nodeFrag from "./shaders/node.frag.glsl?raw";

/**
 * Instanced city node layer — flat-map edition.
 *
 * Positions are projected via Natural Earth onto the XY plane. Nodes
 * remain billboarded toward the camera so they read as bright points
 * regardless of camera tilt. Shockwaves fire on arc landing.
 */
export class NodeLayer {
  readonly object: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;
  private readonly pulseAttr: THREE.InstancedBufferAttribute;
  private readonly pulseColorAttr: THREE.InstancedBufferAttribute;
  private readonly activityAttr: THREE.InstancedBufferAttribute;
  private readonly cityIdx = new Map<string, number>();
  private readonly nearestCityCache = new Map<string, string>();

  constructor() {
    const quad = new THREE.PlaneGeometry(2, 2);
    const geo = new THREE.InstancedBufferGeometry();
    geo.index = quad.index;
    geo.setAttribute("position", quad.getAttribute("position"));
    geo.setAttribute("uv", quad.getAttribute("uv"));

    const count = CITIES.length;
    const positions = new Float32Array(count * 3);
    const phases = new Float32Array(count);
    const tiers = new Float32Array(count);
    const pulses = new Float32Array(count);
    const pulseColors = new Float32Array(count * 3);
    // Start all cities at 0.5 activity so they're visible before first update.
    const activities = new Float32Array(count).fill(0.5);

    // Default pulse color = the flow gold, so any legacy caller without a
    // color still renders the familiar gold ring.
    const defaultR = ((colorNum.flowPrimary >> 16) & 0xff) / 255;
    const defaultG = ((colorNum.flowPrimary >> 8) & 0xff) / 255;
    const defaultB = (colorNum.flowPrimary & 0xff) / 255;

    CITIES.forEach((c, i) => {
      const [x, y] = latLngToPlane(c.lat, c.lng);
      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = 0.006;
      phases[i] = Math.random();
      tiers[i] = c.tier;
      pulses[i] = -10_000;
      pulseColors[i * 3] = defaultR;
      pulseColors[i * 3 + 1] = defaultG;
      pulseColors[i * 3 + 2] = defaultB;
      this.cityIdx.set(`${c.country}:${c.city ?? ""}`, i);
    });

    geo.setAttribute("iPosition", new THREE.InstancedBufferAttribute(positions, 3));
    geo.setAttribute("iPhase", new THREE.InstancedBufferAttribute(phases, 1));
    geo.setAttribute("iTier", new THREE.InstancedBufferAttribute(tiers, 1));
    this.pulseAttr = new THREE.InstancedBufferAttribute(pulses, 1);
    this.pulseAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute("iPulse", this.pulseAttr);
    this.pulseColorAttr = new THREE.InstancedBufferAttribute(pulseColors, 3);
    this.pulseColorAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute("iPulseColor", this.pulseColorAttr);
    this.activityAttr = new THREE.InstancedBufferAttribute(activities, 1);
    this.activityAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute("iActivity", this.activityAttr);
    geo.instanceCount = count;

    this.material = new THREE.ShaderMaterial({
      vertexShader: nodeVert,
      fragmentShader: nodeFrag,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        // Shared scale knob — nodes are sized in world units.
        uRadius: { value: 1 },
        uPixelRatio: { value: window.devicePixelRatio },
        uPulseDuration: { value: 1400 },
        uColor: { value: new THREE.Color(colorNum.flowPrimary) },
      },
    });

    this.object = new THREE.Mesh(geo, this.material);
    this.object.frustumCulled = false;
    this.object.renderOrder = 2;
  }

  /**
   * Fire the shockwave for the city nearest the given lat/lng.
   * Optional colorHex (e.g. 0xF4C770) drives the ring color for product
   * differentiation — gold for send, mint for deposits, violet for
   * conversions. Omit to keep the previous ring color at that city.
   */
  pulseAt(lat: number, lng: number, now: number, colorHex?: number) {
    const idx = this.nearestCity(lat, lng);
    if (idx < 0) return;
    this.pulseAttr.array[idx] = now;
    this.pulseAttr.needsUpdate = true;
    if (colorHex !== undefined) {
      const r = ((colorHex >> 16) & 0xff) / 255;
      const g = ((colorHex >> 8) & 0xff) / 255;
      const b = (colorHex & 0xff) / 255;
      this.pulseColorAttr.array[idx * 3] = r;
      this.pulseColorAttr.array[idx * 3 + 1] = g;
      this.pulseColorAttr.array[idx * 3 + 2] = b;
      this.pulseColorAttr.needsUpdate = true;
    }
  }

  /** Update glow activity for a single city by index (0..CITIES.length-1). */
  setActivity(cityIndex: number, activity: number) {
    if (cityIndex < 0 || cityIndex >= CITIES.length) return;
    (this.activityAttr.array as Float32Array)[cityIndex] = activity;
    this.activityAttr.needsUpdate = true;
  }

  /**
   * Batch-update ALL cities' activity at once.
   * activities must have length >= CITIES.length; index maps to CITIES index.
   */
  setActivityBatch(activities: Float32Array) {
    const arr = this.activityAttr.array as Float32Array;
    const len = Math.min(activities.length, CITIES.length);
    for (let i = 0; i < len; i++) arr[i] = activities[i];
    this.activityAttr.needsUpdate = true;
  }

  update(timeMs: number) {
    this.material.uniforms.uTime.value = timeMs;
  }

  private nearestCity(lat: number, lng: number): number {
    const key = `${lat.toFixed(2)},${lng.toFixed(2)}`;
    const cached = this.nearestCityCache.get(key);
    if (cached !== undefined) {
      return this.cityIdx.get(cached) ?? -1;
    }

    const [tx, ty] = latLngToPlane(lat, lng);
    let bestIdx = -1;
    let bestDist = Infinity;
    let bestKey = "";
    for (let i = 0; i < CITIES.length; i++) {
      const c = CITIES[i];
      const [cx, cy] = latLngToPlane(c.lat, c.lng);
      const d = (cx - tx) ** 2 + (cy - ty) ** 2;
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
        bestKey = `${c.country}:${c.city ?? ""}`;
      }
    }
    if (bestIdx >= 0) this.nearestCityCache.set(key, bestKey);
    return bestIdx;
  }
}
